import { Logger } from '@calendly/logging-service'

import Settings from 'core/settings'

const SUBMIT_URL = 'https://dfp.calendly.com'
const TELEMETRY_REQUEST_TIMEOUT_MS = 5000
const STYTCH_TELEMETRY_FAILED_ERROR_ID = '4fd394a2-bc99-47c5-86d2-64414ee3d1db'
const MAX_FETCH_RETRIES = 3
const RETRY_DELAY_MS = 300

/**
 * Generates a Stytch telemetry ID for fraud detection
 * @param {Object} options - Options for generation
 * @param {string} options.source - Source of the request for logging (e.g., 'book_on_behalf')
 * @returns {Promise<{telemetryId: string|null, failureReason: string|null}>}
 */
export const generateTelemetryId = async ({ source = 'unknown' } = {}) => {
  const publicToken = Settings?.trust_and_safety?.stytch_public_token

  // Pre-call validation: check if public token exists
  if (!publicToken) {
    Logger.warn('[book_on_behalf_telemetry] missing_public_token', { source })
    return { telemetryId: null, failureReason: 'missing_public_token' }
  }

  // Pre-call validation: check if SDK is loaded
  if (typeof window.GetTelemetryID !== 'function') {
    Logger.warn('[book_on_behalf_telemetry] sdk_not_loaded', { source })
    return { telemetryId: null, failureReason: 'sdk_not_loaded' }
  }

  /**
   * Fetches telemetry ID with retry logic for known Stytch error ID.
   * Stytch returns a specific UUID when telemetry generation fails internally.
   */
  const fetchWithRetry = async (retryCount = 0) => {
    const telemetryId = await window.GetTelemetryID({
      submitURL: SUBMIT_URL,
      publicToken
    })

    // Check for known Stytch error ID and retry
    if (telemetryId === STYTCH_TELEMETRY_FAILED_ERROR_ID && retryCount < MAX_FETCH_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
      return fetchWithRetry(retryCount + 1)
    }

    return telemetryId
  }

  try {
    const telemetryId = await Promise.race([
      fetchWithRetry(0),
      new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), TELEMETRY_REQUEST_TIMEOUT_MS))
    ])

    // Log if telemetry ID is falsy or is the known error ID
    if (!telemetryId) {
      Logger.warn('[book_on_behalf_telemetry] falsy_response', { source, telemetryId })
      return { telemetryId: null, failureReason: 'falsy_response' }
    }

    if (telemetryId === STYTCH_TELEMETRY_FAILED_ERROR_ID) {
      Logger.warn('[book_on_behalf_telemetry] stytch_error_id_after_retries', { source, telemetryId })
      return { telemetryId: null, failureReason: 'stytch_error_id' }
    }

    return { telemetryId, failureReason: null }
  } catch (error) {
    const failureReason = error.message === 'timeout' ? 'timeout' : 'unknown'

    Logger.warn(`[book_on_behalf_telemetry] ${failureReason}`, {
      source,
      errorMessage: error.message
    })

    return { telemetryId: null, failureReason }
  }
}
